import type * as React from 'react';
import { Slot } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/** The three surfaces everything sits on: `Card` (white fading to #FAFAFA, shadowed), `Tray` (recessed), `Raised` (a row lifted inside a tray). `asChild` puts the surface on its child (a `motion.section`, a `Field`). */

const cardVariants = cva('dw-card', {
  variants: {
    radius: { lg: 'rounded-card', md: 'rounded-panel', sm: 'rounded-control' },
    padding: { none: '', sm: 'p-(--dw-card-pad-sm)', md: 'p-(--dw-card-pad)', lg: 'p-(--dw-card-pad-lg)' },
    interactive: { true: 'dw-card-interactive' },
  },
  defaultVariants: { radius: 'lg', padding: 'none' },
});

type SurfaceProps = React.ComponentProps<'div'> & { asChild?: boolean };

/** A card: separation by shadow, never an outline. `interactive` lifts 2px on hover. */
export function Card({ className, radius, padding, interactive, asChild = false, ...props }: SurfaceProps & VariantProps<typeof cardVariants>) {
  const Comp = asChild ? Slot.Root : 'div';
  return <Comp className={cn(cardVariants({ radius, padding, interactive }), className)} {...props} />;
}

/** A recessed tray: one step darker than what it sits on. `padded` = the kit's 6px; `deep` = a tray on a tray. */
export function Tray({ className, padded, deep, asChild = false, ...props }: SurfaceProps & { padded?: boolean; deep?: boolean }) {
  const Comp = asChild ? Slot.Root : 'div';
  return <Comp className={cn('rounded-tray shadow-tray', deep ? 'bg-tray-deep' : 'bg-tray', padded && 'p-1.5', className)} {...props} />;
}

/** A raised row inside a tray. `interactive` lifts 1px on hover and sinks 1px on press. `radius="item"` is the 15px of an item in a padded tray. */
export function Raised({ className, interactive, radius = 'control', asChild = false, ...props }: SurfaceProps & { interactive?: boolean; radius?: 'control' | 'item' }) {
  const Comp = asChild ? Slot.Root : 'div';
  return <Comp className={cn('dw-raised', radius === 'item' ? 'rounded-[15px]' : 'rounded-control', interactive && 'dw-raised-interactive', className)} {...props} />;
}
